import { useState, useRef, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";

type ImageWithPreviewProps = Record<string, any> & {
  src?: string | Blob | any;
  previewSrc?: string | Blob | any;
};

export function ImageWithPreview({ src, alt, className, previewSrc, ...props }: ImageWithPreviewProps) {
  const [isOpen, setIsOpen] = useState(false);

  // ── Zoom / pan state ──────────────────────────────────────────
  // Keep both useState (for rendering) AND useRef (for sync read in touch handlers)
  const [scale, setScaleState] = useState(1);
  const [offset, setOffsetState] = useState({ x: 0, y: 0 });

  const scaleRef = useRef(1);           // always-current scale
  const offsetRef = useRef({ x: 0, y: 0 }); // always-current offset

  const setScale = (v: number | ((prev: number) => number)) => {
    setScaleState(prev => {
      const next = typeof v === 'function' ? v(prev) : v;
      scaleRef.current = next;
      return next;
    });
  };

  const setOffset = (v: { x: number; y: number }) => {
    offsetRef.current = v;
    setOffsetState(v);
  };

  // Touch tracking refs (never cause re-renders)
  const lastDistance = useRef<number | null>(null);
  const dragStartTouch = useRef({ x: 0, y: 0 });
  const dragStartOffset = useRef({ x: 0, y: 0 });
  const isPinching = useRef(false);
  const isDragging = useRef(false);
  const lastTapTime = useRef(0);

  const displaySrc = src instanceof Blob ? URL.createObjectURL(src) : (typeof src === 'string' ? src : undefined);
  const displayPreview = previewSrc instanceof Blob ? URL.createObjectURL(previewSrc) : (typeof previewSrc === 'string' ? previewSrc : displaySrc);

  const resetZoom = useCallback(() => {
    scaleRef.current = 1;
    offsetRef.current = { x: 0, y: 0 };
    setScaleState(1);
    setOffsetState({ x: 0, y: 0 });
    lastDistance.current = null;
    isDragging.current = false;
    isPinching.current = false;
  }, []);

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    resetZoom();
    setIsOpen(true);
  };

  const handleClose = () => {
    resetZoom();
    setIsOpen(false);
  };

  // ── Touch distance helper ─────────────────────────────────────
  const getTouchDistance = (touches: React.TouchList) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // ── Touch handlers ────────────────────────────────────────────
  const handleTouchStart = (e: React.TouchEvent) => {
    e.stopPropagation();

    if (e.touches.length === 2) {
      // Begin pinch
      isPinching.current = true;
      isDragging.current = false;
      lastDistance.current = getTouchDistance(e.touches);
    } else if (e.touches.length === 1) {
      isPinching.current = false;
      // Always enable drag — scaleRef.current is the sync value
      isDragging.current = true;
      dragStartTouch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      dragStartOffset.current = { ...offsetRef.current };
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.touches.length === 2 && isPinching.current && lastDistance.current !== null) {
      // ── Pinch-to-zoom ──
      const newDist = getTouchDistance(e.touches);
      const ratio = newDist / lastDistance.current;
      const newScale = Math.min(Math.max(scaleRef.current * ratio, 1), 5);
      scaleRef.current = newScale;
      setScaleState(newScale);
      lastDistance.current = newDist;

      // If pinched back to 1, reset pan
      if (newScale <= 1) {
        offsetRef.current = { x: 0, y: 0 };
        setOffsetState({ x: 0, y: 0 });
      }
    } else if (e.touches.length === 1 && isDragging.current && scaleRef.current > 1) {
      // ── Pan when zoomed ──
      const dx = e.touches[0].clientX - dragStartTouch.current.x;
      const dy = e.touches[0].clientY - dragStartTouch.current.y;
      const newOffset = {
        x: dragStartOffset.current.x + dx,
        y: dragStartOffset.current.y + dy,
      };
      offsetRef.current = newOffset;
      setOffsetState(newOffset);
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    e.stopPropagation();
    if (e.touches.length < 2) {
      isPinching.current = false;
      lastDistance.current = null;
    }
    if (e.touches.length === 0) {
      isDragging.current = false;
      // Snap back to origin if zoomed back to 1
      if (scaleRef.current <= 1) {
        offsetRef.current = { x: 0, y: 0 };
        setOffsetState({ x: 0, y: 0 });
      }
    }
  };

  // ── Mouse Handlers (Desktop 2D Pan) ──────────────────────────
  const handleMouseDown = (e: React.MouseEvent) => {
    if (scaleRef.current <= 1) return;
    e.preventDefault();
    e.stopPropagation();
    isDragging.current = true;
    dragStartTouch.current = { x: e.clientX, y: e.clientY };
    dragStartOffset.current = { ...offsetRef.current };
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging.current || scaleRef.current <= 1) return;
    e.preventDefault();
    e.stopPropagation();
    const dx = e.clientX - dragStartTouch.current.x;
    const dy = e.clientY - dragStartTouch.current.y;
    const newOffset = {
      x: dragStartOffset.current.x + dx,
      y: dragStartOffset.current.y + dy,
    };
    offsetRef.current = newOffset;
    setOffsetState(newOffset);
  };

  const handleMouseUp = () => {
    isDragging.current = false;
  };

  // ── Double-tap to zoom in/out ─────────────────────────────────
  const handleTap = (e: React.MouseEvent | React.TouchEvent) => {
    // Only fire on real taps, not after a drag
    const now = Date.now();
    if (now - lastTapTime.current < 300) {
      if (scaleRef.current > 1) {
        resetZoom();
      } else {
        // Zoom to 2.5× — also reset pan so the centre is shown
        scaleRef.current = 2.5;
        offsetRef.current = { x: 0, y: 0 };
        setScaleState(2.5);
        setOffsetState({ x: 0, y: 0 });
      }
    }
    lastTapTime.current = now;
  };

  // ── Escape key ────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') handleClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen]);

  if (!displaySrc) return null;

  return (
    <>
      <img
        src={displaySrc}
        alt={alt}
        className={`${className} cursor-zoom-in`}
        onClick={handleOpen}
        {...(props as any)}
      />

      {createPortal(
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={{ zIndex: 2147483647, isolation: 'isolate' }}
            className="fixed inset-0 flex items-center justify-center bg-black/95 backdrop-blur-sm"
            onClick={(e) => {
              // Backdrop tap closes only when not zoomed
              if (scaleRef.current <= 1 && e.target === e.currentTarget) handleClose();
            }}
          >
            {/* Zoom / pan container */}
            <div
              className="relative flex items-center justify-center select-none"
              style={{
                transform: `scale(${scale}) translate(${offset.x / scale}px, ${offset.y / scale}px)`,
                transformOrigin: 'center center',
                touchAction: 'none',
                cursor: scale > 1 ? (isDragging.current ? 'grabbing' : 'grab') : 'default',
                willChange: 'transform',
              }}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onClick={handleTap}
            >
              <motion.img
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                src={displayPreview}
                alt={alt}
                draggable={false}
                className="max-w-[95vw] max-h-[90vh] object-contain rounded-lg shadow-2xl pointer-events-none select-none"
              />

              {/* ── X Button — top-right of image, dark bg + blue border ── */}
              <button
                onClick={(e) => { e.stopPropagation(); handleClose(); }}
                style={{ zIndex: 100000 }}
                className="absolute top-2 right-2 w-9 h-9 rounded-lg bg-black/70 border-2 border-[#4478ea] flex items-center justify-center text-white hover:bg-black/90 transition-colors shadow-lg backdrop-blur-sm"
                aria-label="Close image"
              >
                <X className="w-4 h-4" strokeWidth={2.5} />
              </button>
            </div>

            {/* Zoom hint — fades away automatically */}
            {scale === 1 && (
              <motion.p
                initial={{ opacity: 0.7 }}
                animate={{ opacity: 0 }}
                transition={{ delay: 1.8, duration: 0.8 }}
                className="absolute bottom-6 left-0 right-0 text-center text-white/50 text-xs pointer-events-none"
              >
                Pinch or double-tap to zoom · drag to pan
              </motion.p>
            )}
          </motion.div>
        )}
      </AnimatePresence>,
      document.body
      )}
    </>
  );
}
